import React, {useEffect, useMemo} from 'react';
import {Navigate, Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import * as Sentry from "@sentry/react";
import AssetRoutes from "./assets";
import SystemRoutes from "./systems";
import {useTranslation} from "react-i18next";
import InstalledBaseRoutes from "./installedBase";
import ConfigurationsRoutes, {getConfigurationTabs} from "./configurations";
import {getUserResources, Header, HeaderMenuItem} from "@dreebit-registry/vsm-microservice-components";
import {useKeycloak} from "@react-keycloak/web";
import {Button} from "@mui/material";
import {AddRounded, SettingsFilledRounded} from "@mui-symbols-material/w300";
import RegisterAssetModal from "../components/RegisterAssetModal";
import {IconButton} from "@mui/joy";
import Dashboard from '../components/Dashboard';
import {localLogout} from "../utils/localLogout";
import GridViewRoutes from "./gridView";
import ImportRoutes from "./import";
import ProfileRoutes from "./profile";
import {shouldShowSettingsIcon} from "../utils/shouldShowSettingsIcon";
import NovuNotificationBell from "../components/NovuNotificationBell";
import NotificationsProvider from "../components/NotificationsProvider";
import EventsRoutes from "./events";
import RiskAssessmentReportRoutes from "./riskAssessmentReports";
import {isRiskReportsEnabled} from "../utils/riskReportsEnabled";
import ContaminationDeclarationRoutes from "./contaminationDeclarations";
import {isContaminationDeclarationsEnabled} from "../utils/contaminationDeclarationsEnabled";
import {IndustryType, useAppName, useIndustryType} from "../hooks/useIndustryType";
import SystemAnnouncements from "../components/SystemAnnouncements";
import {jwtDecode} from "jwt-decode";
import _ from "lodash";

// Dashboard, Assets, Installed Base plus Systems (INDUSTRY) or Events (SEMICONDUCTOR) —
// everything after this goes into "More".
const PRIMARY_NAV_ITEM_COUNT = 4;

const RouteWrapper = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const {keycloak} = useKeycloak();
    const appName = useAppName();
    const industryType = useIndustryType();
    const resources = getUserResources(keycloak.token, 'otto_installed_base')
    const hasImportAccess = resources.includes("assetLocationImport")

    // The flat list stays the source of truth for activeId and document.title — both
    // match on `route`, which the "More" group deliberately does not have.
    const flatMenuItems = useMemo(() => {
        const dashboard = {
            id: 'dashboard',
            title: t('Dashboard'),
            route: '/dashboard',
            htmlTitle: `${t('Dashboard')} | ${appName} | ${t('Installed Base')}`
        }
        const assets = {
            id: 'assets',
            title: t('Assets'),
            route: '/assets',
            htmlTitle: `${t('Assets')} | ${appName} | ${t('Installed Base')}`
        }
        const systems = {
            id: 'systems',
            title: t('Systems'),
            route: '/systems',
            htmlTitle: `${t('Systems')} | ${appName} | ${t('Installed Base')}`
        }
        const installedBase = {
            id: 'installedBase',
            title: t('Installed Base'),
            route: '/installedBase',
            htmlTitle: `${t('Installed Base')} | ${appName} | ${t('Installed Base')}`
        }
        const gridViews = {
            id: 'gridViews',
            title: t('Grid Views'),
            route: '/gridViews',
            htmlTitle: `${t('Grid Views')} | ${appName} | ${t('Installed Base')}`
        }
        const events = {
            id: 'events',
            title: t('Events'),
            route: '/events',
            htmlTitle: `${t('Events')} | ${appName} | ${t('Installed Base')}`
        }
        // Semiconductor users work with events far more than with systems, so the two swap
        // between the header bar and "More".
        const items = industryType === IndustryType.SEMICONDUCTOR
            ? [dashboard, assets, installedBase, events, systems, gridViews]
            : [dashboard, assets, systems, installedBase, gridViews, events]
        if (hasImportAccess) {
            items.push({
                id: 'import',
                title: t('Import'),
                route: '/import',
                htmlTitle: `${t('Import')} | ${appName} | ${t('Installed Base')}`
            })
        }
        // Feature-flagged: on for testing/local only while the backend is in
        // development (see src/utils/riskReportsEnabled.ts).
        if (isRiskReportsEnabled()) {
            items.push({
                id: 'riskAssessmentReports',
                title: t('Risk assessment reports'),
                route: '/riskAssessmentReports',
                htmlTitle: `${t('Risk assessment reports')} | ${appName} | ${t('Installed Base')}`
            })
        }
        return items
    }, [t, appName, industryType, hasImportAccess])

    // Only the first four entries stay in the header bar; everything else collapses
    // into a "More" dropdown. The library resolves the group back into its individual
    // entries inside the overflow drawer on narrow screens.
    const headerMenuConfig: HeaderMenuItem[] = useMemo(() => {
        const collapsed = flatMenuItems.slice(PRIMARY_NAV_ITEM_COUNT)
        return collapsed.length
            ? [
                ...flatMenuItems.slice(0, PRIMARY_NAV_ITEM_COUNT),
                {id: 'more', title: t('More'), subItems: collapsed}
            ]
            : flatMenuItems
    }, [flatMenuItems, t])

    const roles = useMemo<string[]>(() => {
        if (!keycloak.token) return [];
        try {
            const decoded = jwtDecode<Record<string, unknown>>(keycloak.token);
            return _.get(decoded, 'otto_access.roles', []) as string[];
        } catch { return []; }
    }, [keycloak.token]);

    const allTabs = useMemo(() => getConfigurationTabs(t), [t]);
    const showSettings = shouldShowSettingsIcon(allTabs, resources, roles);

    const settingsIcon = showSettings ?
        <IconButton
            onClick={() => {
                navigate('/configurations');
            }}
            sx={{
                mr: "12px",
                color: "#333",
                width: 36,
                height: 36,
                minWidth: 36,
                minHeight: 36,
                padding: "6px",
                borderRadius: "50%",
                boxSizing: "border-box",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            }}
        >
            <SettingsFilledRounded/>
        </IconButton> : undefined

    const headerRightIcons = <>
        <NovuNotificationBell />
        {settingsIcon}
    </>

    const faqLink = process.env.REACT_APP_PUBLIC_FAQ_URL
    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true"

    const activeMenuItem = flatMenuItems.find(item => location.pathname.startsWith(item.route));
    const activeId = activeMenuItem?.id ?? '';

    const [registerModalOpen, setRegisterModalOpen] = React.useState(false);

    // Track navigation changes as Sentry breadcrumbs
    // Exclude /crm and /catalog routes (separate microservices)
    useEffect(() => {
        const isExcludedRoute = location.pathname.startsWith("/crm") || location.pathname.startsWith("/catalog");
        if (!isExcludedRoute) {
            Sentry.addBreadcrumb({
                category: "navigation",
                message: `Navigated to ${location.pathname}`,
                data: {
                    pathname: location.pathname,
                    search: location.search,
                    hash: location.hash,
                },
                level: "info",
            });
        }
    }, [location]);

    useEffect(() => {
        const matchingRouteConfigItem = flatMenuItems.find(x => location.pathname.includes(x.route))

        document.title = matchingRouteConfigItem?.htmlTitle ?? `${appName} | ${t('Installed Base')}`
    }, [flatMenuItems, location, appName, t])

    return (
        <NotificationsProvider>
            <SystemAnnouncements/>
            <Header
                keycloakToken={keycloak.token}
                headerMenuConfig={headerMenuConfig}
                onLogoClick={() => navigate('/dashboard')}
                activeId={activeId.toString()}
                actions={
                    <>
                        <Button
                            size={'small'}
                            startIcon={<AddRounded/>}
                            variant={"contained"}
                            onClick={() => setRegisterModalOpen(true)}
                        >
                            {t('Add Asset')}
                        </Button>
                        {registerModalOpen && <RegisterAssetModal open={registerModalOpen} onClose={() => setRegisterModalOpen(false)}/>}
                    </>
                }
                iconMenuItems={headerRightIcons}
                faqLink={faqLink}
                onLogout={() => localLogout(keycloak)}
                showTestEnvBanner={showEnvBanner}
                onProfileClick={() => navigate('/profile')}
            />
            <div
                className={'full-width content-height flex'}
                style={{height: showEnvBanner ? "calc(100% - 63px - 40px)" : "calc(100% - 63px)"}}
            >
                <Routes>
                    <Route path='/dashboard/*' element={<Dashboard/>} />
                    <Route path='/assets/*' element={<AssetRoutes/>} />
                    <Route path='/systems/*' element={<SystemRoutes/>} />
                    <Route path="/installedBase/*" element={<InstalledBaseRoutes/>} />
                    <Route path="/gridViews/*" element={<GridViewRoutes/>} />
                    <Route path="/events/*" element={<EventsRoutes/>} />
                    {hasImportAccess && <Route path="/import/*" element={<ImportRoutes />} />}
                    {isRiskReportsEnabled() && <Route path="/riskAssessmentReports/*" element={<RiskAssessmentReportRoutes/>} />}
                    {/* Reached via lifecycle events / the asset page only — no header entry (see src/utils/contaminationDeclarationsEnabled.ts). */}
                    {isContaminationDeclarationsEnabled() && <Route path="/contaminationDeclarations/*" element={<ContaminationDeclarationRoutes/>} />}
                    <Route path="/profile/*" element={<ProfileRoutes />} />
                    {showSettings && <Route path="/configurations/*" element={<ConfigurationsRoutes/>} />}
                    <Route path="*" element={<Navigate to="/dashboard" replace/>} />
                </Routes>
            </div>
        </NotificationsProvider>
    );
}

export default RouteWrapper;
